import { useOnClickOutside } from 'custom_hooks/useOnClickOutside';
import * as React from 'react';
import { useCallback, useRef, useState } from 'react';

/**
 * All of the possible actions that can occur within a popup.
 */
export type PopupAction =
    | {
          type: 'close';
          /**
           * The event that caused the popup to close (if it exists).
           */
          sourceEvent?: MouseEvent | TouchEvent;
      }
    | {
          type: 'open';
      };

/**
 * The internal state of the popup.
 */
export type PopupState = {
    open: boolean;
};

/**
 * The info supplied to the `onStateChange` callback when the state changes.
 */
export type PopupStateChangeContext = PopupState & PopupAction;

export type UsePopup = {
    /**
     * Sets the initial open state of the popup. Changing this prop
     * will NOT affect subsequent renders.
     *
     * Defaults to `false`.
     */
    initialOpen?: boolean;
    /**
     * Whether or not to automatically set the popup to closed when
     * a click event is occurs outside of the popup.
     *
     * Defaults to `true`.
     */
    closeOnClickOutside?: boolean;
    /**
     * Callback to call when the state of the popup changes.
     */
    onStateChange?: (context: PopupStateChangeContext) => void;
    /**
     * Additional popup content refs.
     *
     * A click on content ref's element will not cause the popup to close.
     */
    contentRefs?: React.RefObject<HTMLElement>[];
};

export type PopupReturn<
    TriggerElement extends HTMLElement = HTMLButtonElement,
    PopupElement extends HTMLElement = HTMLDivElement,
> = Readonly<{
    isOpen: boolean;
    open: () => void;
    close: () => void;
    toggleOpen: () => void;
    triggerRef: React.RefObject<TriggerElement>;
    popupRef: React.RefObject<PopupElement>;
}>;

/**
 * Creates state to handle common popup behaviors.
 */
export function usePopup<
    TriggerElement extends HTMLElement = HTMLButtonElement,
    PopupElement extends HTMLElement = HTMLDivElement,
>({
    closeOnClickOutside = true,
    initialOpen = false,
    onStateChange,
    contentRefs = [],
}: UsePopup = {}): PopupReturn<TriggerElement, PopupElement> {
    const [isOpen, setOpen] = useState(initialOpen);
    const triggerRef = useRef<TriggerElement>(null);
    const popupRef = useRef<PopupElement>(null);

    const onStateChangeRef = useRef(onStateChange);
    onStateChangeRef.current = onStateChange;

    const open = useCallback(() => {
        setOpen(true);
        onStateChangeRef.current?.({ type: 'open', open: true });
    }, []);
    const close = useCallback(() => {
        setOpen(false);
        onStateChangeRef.current?.({ type: 'close', open: false });
    }, []);
    const toggleOpen = useCallback(() => {
        setOpen((_open) => {
            onStateChangeRef.current?.({ type: 'open', open: !_open });
            return !_open;
        });
    }, []);

    const shouldCloseOnClickOutside = isOpen && closeOnClickOutside;

    useOnClickOutside(
        [triggerRef, popupRef, ...contentRefs],
        (event) => {
            setOpen(false);
            onStateChangeRef.current?.({
                type: 'close',
                open: false,
                sourceEvent: event,
            });
        },
        shouldCloseOnClickOutside,
        isOpen,
    );

    return {
        isOpen,
        open,
        close,
        toggleOpen,
        triggerRef,
        popupRef,
    };
}
